// src/components/FileViewerModal.jsx
import React from "react";
import PdfViewer from "./PdfViewer";

/**
 * FileViewerModal – shared AskLawly in-app file viewer shell.
 *
 * File Access Gateway contract:
 * - Gateway-backed private files stay on AskLawly-controlled URLs.
 * - Authenticated PDF requests pass record.httpHeaders/fileAccessHeaders into PdfViewer.
 * - "Open with browser" can obtain an opaque AskLawly browser ticket at click time.
 * - Gateway-backed PDFs never need a storage-provider URL in this component.
 * - Existing File Vault/share/Office/generic behavior remains compatible while those
 *   products are migrated to the File Access Gateway separately.
 *
 * Viewer ownership:
 * - PDF files render through the dedicated PdfViewer component.
 * - Office files render once through Microsoft Office Online.
 * - Images, HTML, text, and generic URLs keep the existing viewer behavior.
 * - The modal owns minimize, maximize, restore, close, and non-PDF actions.
 * - PdfViewer owns every PDF-specific tool, including search, page navigation,
 *   zoom, rotation, print, download, fullscreen, and browser fallback.
 *
 * Version: 2026.08.30-v2-file-access-gateway
 */

const MOBILE_TABLET_BREAKPOINT = 1024;
const DESKTOP_PADDING = 24;
const MOBILE_TABLET_PADDING = 0;
const MAX_W = 1100;
const MAX_H = 980;
const BACKEND_BASE = (process.env.REACT_APP_BACKEND_URL || window.__BACKEND_BASE__ || "").replace(/\/+$/, "");

export default function FileViewerModal({
  open,
  onClose,
  record,
  onDownload,
  getBrowserFallbackUrl,
  getFreshPdfPreviewUrl,
}) {
  const [zoom, setZoom] = React.useState(100);
  const [downloading, setDownloading] = React.useState(false);
  const [isMinimized, setIsMinimized] = React.useState(false);
  const [isMaximized, setIsMaximized] = React.useState(false);
  const [isMobileTablet, setIsMobileTablet] = React.useState(
    () => window.innerWidth < MOBILE_TABLET_BREAKPOINT
  );

  const viewportPadding = isMobileTablet
    ? MOBILE_TABLET_PADDING
    : DESKTOP_PADDING;

  const [box, setBox] = React.useState(() =>
    calcBox(window.innerWidth, window.innerHeight)
  );
  const prevBoxRef = React.useRef(box);
  const dragStateRef = React.useRef(null);

  const dockRef = React.useRef(null);
  const dockDragRef = React.useRef(null);

  // Reference the visible non-PDF iframe and track the current view kind.
  const viewerFrameRef = React.useRef(null);
  const currentViewKindRef = React.useRef(null); // 'pdf' | 'html-img' | 'office' | 'url' | 'html-generic'
  const printingRef = React.useRef(false);       // guard against rapid double-clicks
  const backdropPointerRef = React.useRef(null); // deliberate backdrop-click guard

  // Reset window state on open + follow the app-wide responsive rule:
  // < 1024px = mobile/tablet; >= 1024px = laptop/desktop.
  React.useEffect(() => {
    if (!open) return;

    const syncViewport = () => {
      const mobileTablet =
        window.innerWidth < MOBILE_TABLET_BREAKPOINT;
      const padding = getViewportPadding(window.innerWidth);

      setIsMobileTablet(mobileTablet);
      setIsMinimized(false);

      if (mobileTablet) {
        setIsMaximized(false);
      }

      setBox(
        calcBox(window.innerWidth, window.innerHeight)
      );

      if (!mobileTablet) {
        setBox((current) =>
          clampBox(
            current,
            window.innerWidth,
            window.innerHeight,
            padding
          )
        );
      }
    };

    setIsMinimized(false);
    setIsMaximized(false);
    syncViewport();

    window.addEventListener("resize", syncViewport);

    return () =>
      window.removeEventListener("resize", syncViewport);
  }, [open]);

  // Lock the underlying app/page while the modal is actively visible.
  // PdfViewer keeps its own internal scrolling, while the page behind the modal
  // cannot drift, overscroll, or jump to a blank-looking position.
  React.useEffect(() => {
    if (!open || isMinimized) return undefined;
    if (typeof window === "undefined" || typeof document === "undefined") {
      return undefined;
    }

    const body = document.body;
    const root = document.documentElement;
    if (!body || !root) return undefined;

    const scrollX = window.scrollX || window.pageXOffset || 0;
    const scrollY = window.scrollY || window.pageYOffset || 0;

    const previous = {
      bodyOverflow: body.style.overflow,
      bodyOverscrollBehavior: body.style.overscrollBehavior,
      rootOverflow: root.style.overflow,
      rootOverscrollBehavior: root.style.overscrollBehavior,
    };

    body.style.overflow = "hidden";
    body.style.overscrollBehavior = "none";
    root.style.overflow = "hidden";
    root.style.overscrollBehavior = "none";

    return () => {
      body.style.overflow = previous.bodyOverflow;
      body.style.overscrollBehavior = previous.bodyOverscrollBehavior;
      root.style.overflow = previous.rootOverflow;
      root.style.overscrollBehavior = previous.rootOverscrollBehavior;

      try {
        window.scrollTo(scrollX, scrollY);
      } catch {}
    };
  }, [open, isMinimized]);

  // ESC close
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // ----- Record fields and format routing -----
  const title = (record?.subject || record?.title || "Document").trim();
  const createdAtLabel = record?.createdAt
    ? new Date(record.createdAt).toLocaleString()
    : null;

  const contentType = String(
    record?.contentType || record?.mimeType || ""
  ).toLowerCase();
  const recordName = String(
    record?.filename ||
      record?.name ||
      record?.title ||
      record?.subject ||
      ""
  ).toLowerCase();
  const recordFormat = String(
    record?.format ||
      record?.fileFormat ||
      record?.documentFormat ||
      ""
  )
    .trim()
    .toLowerCase();

  const htmlBody = record?.html ?? record?.contentHtml ?? null;
  const htmlDoc = record?.htmlDoc ?? null;

  // ----- File Access Gateway record contract -----
  // ChatInterface (and future product adapters) can mark a viewer record as
  // gateway-backed without teaching this modal anything about GCS, buckets,
  // Firestore paths, or product-specific storage namespaces.
  const gatewayBrowserTicketUrl = String(
    record?.browserTicketUrl ||
      record?.fileAccessBrowserTicketUrl ||
      ""
  ).trim();

  const isFileAccessGatewayRecord = Boolean(
    record?.fileAccessGateway === true ||
      record?.gatewayAccess === true ||
      gatewayBrowserTicketUrl
  );

  const fileAccessHttpHeaders =
    record?.httpHeaders ||
    record?.fileAccessHeaders ||
    undefined;

  const fileAccessWithCredentials =
    record?.withCredentials === true ||
    record?.fileAccessWithCredentials === true;

  const gatewayViewerUrl = isFileAccessGatewayRecord
    ? record?.fileAccessUrl ||
      record?.viewerUrl ||
      record?.url ||
      null
    : null;

  // Compatibility with the current FileViewerPage: if that page already wrapped
  // a PDF or Office document inside htmlDoc, unwrap the real source here so the
  // specialized viewer is used and no iframe-inside-an-iframe remains.
  const pdfFromHtmlDoc = htmlDoc ? extractPdfSrcFromHtml(htmlDoc) : null;
  const officeFromHtmlDoc = htmlDoc ? extractOfficeSrcFromHtml(htmlDoc) : null;

  const explicitPdfSource =
    gatewayViewerUrl ||
    record?.pdfBlob ||
    record?.pdfData ||
    record?.urls?.pdf ||
    record?.pdfUrlInline ||
    record?.pdfUrl ||
    pdfFromHtmlDoc ||
    null;

  const pdfByMetadata =
    isPdfContentType(contentType) ||
    /\.pdf$/i.test(recordName) ||
    [
      record?.viewerUrl,
      record?.downloadUrl,
      record?.url,
      record?.urls?.pdf,
    ].some((value) => isPdfUrl(value));

  const pdfSource =
    explicitPdfSource ||
    (pdfByMetadata
      ? record?.viewerUrl ||
        record?.url ||
        record?.downloadUrl ||
        null
      : null);

  const isPdfRecord = Boolean(
    pdfSource &&
      (pdfByMetadata ||
        pdfFromHtmlDoc ||
        record?.urls?.pdf ||
        record?.pdfUrl ||
        record?.pdfUrlInline ||
        record?.pdfBlob ||
        record?.pdfData)
  );

  // File Vault signed URLs are frequently opaque and may not contain ".docx".
  // Trust the explicit Office alias and file metadata instead of requiring the
  // signed URL itself to look like a Word/Excel/PowerPoint URL.
  const explicitOfficeCandidate =
    record?.urls?.docx ||
    record?.docxUrl ||
    record?.officeUrl ||
    null;

  const officeFormatHint =
    recordFormat === "office" ||
    /^(docx?|word|xlsx?|excel|pptx?|powerpoint|rtf)$/i.test(
      recordFormat
    );

  const officeLikely =
    !isPdfRecord &&
    Boolean(
      officeFromHtmlDoc ||
        explicitOfficeCandidate ||
        officeFormatHint ||
        isOfficeContentType(contentType) ||
        /\.(docx?|xlsx?|pptx?|rtf)$/i.test(recordName) ||
        [
          record?.downloadUrl,
          record?.viewerUrl,
          record?.url,
        ].some((value) => isOfficeLikeUrl(value))
    );

  const rawOfficeCandidate =
    !isPdfRecord && officeLikely
      ? explicitOfficeCandidate ||
        record?.downloadUrl ||
        record?.viewerUrl ||
        record?.url ||
        null
      : null;

  // If an existing Office Online URL was supplied, use it directly. Otherwise
  // create exactly one Microsoft Office Online embed around the original file.
  const officeEmbedUrl = officeFromHtmlDoc
    ? decodeHtmlAttribute(officeFromHtmlDoc)
    : rawOfficeCandidate
    ? isOfficeEmbedUrl(rawOfficeCandidate)
      ? rawOfficeCandidate
      : `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
          rawOfficeCandidate
        )}`
    : null;

  const isOfficeRecord = Boolean(officeEmbedUrl);

  const pdfDownloadUrl =
    (isPdfRecord &&
      (isFileAccessGatewayRecord
        ? typeof pdfSource === "string"
          ? pdfSource
          : ""
        : record?.downloadUrl ||
          record?.urls?.pdf ||
          record?.pdfUrl ||
          record?.pdfUrlInline ||
          (typeof pdfSource === "string" ? pdfSource : ""))) ||
    "";

  // Choose the download asset by the active format. This prevents a Word file
  // from accidentally selecting an associated PDF URL, and vice versa.
  const downloadUrl = isPdfRecord
    ? pdfDownloadUrl
    : isOfficeRecord
    ? record?.downloadUrl ||
      record?.urls?.docx ||
      record?.url ||
      record?.viewerUrl ||
      null
    : record?.downloadUrl ||
      record?.urls?.text ||
      record?.urls?.html ||
      record?.urls?.png ||
      record?.urls?.jpg ||
      record?.url ||
      record?.viewerUrl ||
      null;

  const genericUrl = React.useMemo(() => {
    if (isPdfRecord || isOfficeRecord) return null;

    return (
      record?.viewerUrl ||
      record?.downloadUrl ||
      record?.url ||
      record?.urls?.html ||
      record?.urls?.text ||
      record?.urls?.png ||
      record?.urls?.jpg ||
      null
    );
  }, [record, isPdfRecord, isOfficeRecord]);

  const browserFallbackProvider = React.useMemo(() => {
    // Gateway-backed records use an AskLawly browser ticket. The authenticated
    // ticket request happens only when the user explicitly chooses
    // "Open with browser". The returned ticketPath is resolved against the
    // ticket endpoint's origin, so this component does not hard-code .com/.ai/.io.
    if (isFileAccessGatewayRecord && gatewayBrowserTicketUrl) {
      return async () => {
        const response = await fetch(gatewayBrowserTicketUrl, {
          method: "POST",
          headers: fileAccessHttpHeaders,
          credentials: fileAccessWithCredentials ? "include" : "omit",
          mode: "cors",
        });

        if (!response.ok) {
          throw new Error(
            `Browser file access request failed (${response.status}).`
          );
        }

        const payload = await response.json().catch(() => null);
        const ticketPath = String(
          payload?.ticketPath || payload?.path || ""
        ).trim();

        if (!ticketPath) {
          throw new Error(
            "Browser file access did not return an AskLawly ticket."
          );
        }

        try {
          return new URL(ticketPath, gatewayBrowserTicketUrl).toString();
        } catch {
          throw new Error(
            "Browser file access returned an invalid AskLawly ticket."
          );
        }
      };
    }

    const provider =
      getBrowserFallbackUrl ||
      getFreshPdfPreviewUrl ||
      record?.getBrowserFallbackUrl ||
      record?.getFreshPdfPreviewUrl;

    if (typeof provider !== "function") return undefined;

    return (context) =>
      provider({
        ...context,
        record,
      });
  }, [
    getBrowserFallbackUrl,
    getFreshPdfPreviewUrl,
    record,
    isFileAccessGatewayRecord,
    gatewayBrowserTicketUrl,
    fileAccessHttpHeaders,
    fileAccessWithCredentials,
  ]);

  // Never let a gateway-backed record fall through to a legacy direct URL for
  // browser fallback. It must use the opaque AskLawly ticket provider above.
  const explicitBrowserFallbackUrl = isFileAccessGatewayRecord
    ? ""
    : record?.browserFallbackUrl || record?.fallbackUrl || "";

  const allowRecordDownload =
    record?.allowDownload !== false &&
    record?.linkAllowDownload !== false;

  // Allow download when we have either a direct URL or a File Vault node.
  const canDownload = React.useMemo(() => {
    if (!allowRecordDownload) return false;

    try {
      const qs = new URLSearchParams(window.location.search);
      return Boolean(
        downloadUrl ||
          record?.id ||
          record?.nodeId ||
          qs.get("nodeId")
      );
    } catch {
      return Boolean(downloadUrl);
    }
  }, [downloadUrl, record, allowRecordDownload]);

  const handlePdfDownload = React.useCallback(
    async () => {
      if (typeof onDownload !== "function") return;

      await onDownload(
        "pdf",
        pdfDownloadUrl ||
          (typeof pdfSource === "string" ? pdfSource : "")
      );
    },
    [onDownload, pdfDownloadUrl, pdfSource]
  );

  // ----- Flicker guard: delay showing the fallback message -----
  const [fallbackDelayPassed, setFallbackDelayPassed] = React.useState(false);
  React.useEffect(() => {
    setFallbackDelayPassed(false);
    const t = setTimeout(() => setFallbackDelayPassed(true), 600);
    return () => clearTimeout(t);
  }, [record]);

  // ----- Styles -----
  const s = React.useMemo(
    () => ({
      backdrop: {
        position: "fixed",
        inset: 0,
        // Keep the same dimmed modal backdrop on desktop, tablet, and mobile.
        // The viewer itself remains full-screen on mobile/tablet, but the modal
        // contract stays visually consistent across every device size.
        background: "rgba(17,24,39,0.50)",
        display: open && !isMinimized ? "block" : "none",
        zIndex: 10000,
        padding: `${viewportPadding}px`,
        boxSizing: "border-box",
        overscrollBehavior: "contain",
      },
      modal: {
        position: "absolute",
        left: `${
          isMobileTablet
            ? 0
            : isMaximized
            ? viewportPadding
            : box.left
        }px`,
        top: `${
          isMobileTablet
            ? 0
            : isMaximized
            ? viewportPadding
            : box.top
        }px`,
        width: `${
          isMobileTablet
            ? window.innerWidth
            : isMaximized
            ? window.innerWidth - 2 * viewportPadding
            : box.width
        }px`,
        height: `${
          isMobileTablet
            ? window.innerHeight
            : isMaximized
            ? window.innerHeight - 2 * viewportPadding
            : box.height
        }px`,
        background: "#fff",
        borderRadius: isMobileTablet ? 0 : 16,
        border: isMobileTablet
          ? "0"
          : "1px solid #e5e7eb",
        display: "grid",
        gridTemplateRows: isPdfRecord ? "auto minmax(0, 1fr)" : "auto auto minmax(0, 1fr)",
        overflow: "hidden",
        boxShadow: isMobileTablet
          ? "none"
          : "0 20px 60px rgba(0,0,0,0.25)",
        userSelect: dragStateRef.current ? "none" : "auto",
      },
      header: {
        minWidth: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: isMobileTablet ? 8 : 12,
        padding: isMobileTablet
          ? "7px 8px 7px 10px"
          : "10px 14px",
        borderBottom: "1px solid #e5e7eb",
        background: "#f9fafb",
        cursor:
          isMobileTablet || isMaximized
            ? "default"
            : "grab",
        touchAction: isMobileTablet ? "auto" : "none",
      },
      titleWrap: { display: "grid", gap: 2, flex: 1, minWidth: 0 },
      titleStyle: {
        fontSize: isMobileTablet ? 14 : 16,
        fontWeight: 700,
        color: "#111827",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      },
      meta: {
        fontSize: isMobileTablet ? 10 : 12,
        color: "#6b7280",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      },
      winControls: {
        display: "flex",
        alignItems: "center",
        gap: isMobileTablet ? 4 : 6,
        flex: "0 0 auto",
      },
      ctrlBtn: {
        width: isMobileTablet ? 34 : 32,
        height: isMobileTablet ? 32 : 28,
        display: "grid",
        placeItems: "center",
        border: "1px solid #d1d5db",
        borderRadius: 8,
        background: "#fff",
        cursor: "pointer",
        userSelect: "none",
        fontWeight: 700,
        lineHeight: 1,
      },
      ctrlClose: { color: "#ef4444", borderColor: "#ef4444" },

      toolbar: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        padding: "8px 12px",
        borderBottom: "1px solid #f3f4f6",
        background: "#fff",
        flexWrap: "wrap",
      },
      leftGroup: { display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" },
      rightGroup: { display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" },
      pill: {
        fontSize: 12,
        color: "#374151",
        background: "#f3f4f6",
        border: "1px solid #e5e7eb",
        padding: "6px 10px",
        borderRadius: 999,
        whiteSpace: "nowrap",
      },
      btn: {
        border: "1px solid #d1d5db",
        background: "#fff",
        borderRadius: 10,
        padding: "8px 12px",
        cursor: "pointer",
        fontWeight: 600,
      },
      primary: {
        border: "1px solid #111827",
        background: "#111827",
        color: "#fff",
        borderRadius: 10,
        padding: "8px 12px",
        cursor: "pointer",
        fontWeight: 700,
      },
      contentWrap: {
        position: "relative",
        minWidth: 0,
        minHeight: 0,
        background: isPdfRecord ? "#242424" : "#f3f4f6",
        overflow: isPdfRecord ? "hidden" : "auto",
      },
      pdfViewerWrap: {
        width: "100%",
        height: "100%",
        minWidth: 0,
        minHeight: 0,
        overflow: "hidden",
        background: "#242424",
      },
      pageCenter: {
        minHeight: "100%",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        padding: isMobileTablet ? 6 : 16,
      },
      pageOuter: { transform: `scale(${zoom / 100})`, transformOrigin: "top center" },
      page: {
        width: "8.5in",
        minHeight: "11in",
        background: "#fff",
        color: "#111827",
        border: "1px solid #e5e7eb",
        borderRadius: 6,
        boxShadow: "0 2px 16px rgba(0,0,0,0.08)",
        padding: "1in",
      },
      iframeWrap: { width: "100%", height: "100%", overflow: "hidden", display: "flex" },
      iframeOuterZoomable: {
        width: "100%",
        height: "100%",
        transform: `scale(${zoom / 100})`,
        transformOrigin: "top center",
      },
      iframe: { width: "100%", height: "100%", border: 0, background: "#fff" },

      // Minimized dock
      dock: {
        position: "fixed",
        right: 16,
        bottom: 16,
        zIndex: 10001,
        background: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        padding: "6px 10px",
        boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
        display: isMinimized ? "flex" : "none",
        alignItems: "center",
        gap: 8,
        maxWidth: "80vw",
      },
      dockHandle: {
        width: 14,
        height: 24,
        cursor: "grab",
        borderRight: "1px solid #e5e7eb",
        marginRight: 8,
        display: "grid",
        placeItems: "center",
        color: "#9ca3af",
        userSelect: "none",
      },
      dockTitle: {
        fontSize: 13,
        fontWeight: 600,
        color: "#111827",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        maxWidth: "50vw",
      },
      dockBtn: {
        fontSize: 12,
        border: "1px solid #d1d5db",
        background: "#fff",
        borderRadius: 8,
        padding: "6px 8px",
        cursor: "pointer",
      },
      loading: {
        padding: 24,
        color: "#6b7280",
        fontSize: 14,
        textAlign: "center",
      },
    }),
    [
      open,
      box,
      zoom,
      isMaximized,
      isMinimized,
      isPdfRecord,
      isMobileTablet,
      viewportPadding,
    ]
  );

  // ----- Drag (modal) -----
  const onDragStart = (e) => {
    if (!open) return;
    if (isMobileTablet || isMaximized) return;
    try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch {}
    dragStateRef.current = { offsetX: e.clientX - box.left, offsetY: e.clientY - box.top };
  };
  const onDragMove = (e) => {
    if (!dragStateRef.current) return;
    const vw = window.innerWidth, vh = window.innerHeight;
    const left = e.clientX - dragStateRef.current.offsetX;
    const top  = e.clientY - dragStateRef.current.offsetY;
    setBox((b) =>
      clampTo(
        left,
        top,
        b.width,
        b.height,
        vw,
        vh,
        viewportPadding
      )
    );
  };
  const onDragEnd = (e) => {
    try { e.currentTarget.releasePointerCapture?.(e.pointerId); } catch {}
    dragStateRef.current = null;
  };

  // ----- Window controls -----
  const doClose = (e) => { e?.stopPropagation?.(); onClose?.(); };
  const doMinimize = (e) => { e?.stopPropagation?.(); setIsMinimized(true); };
  const doMaximize = (e) => {
    e?.stopPropagation?.();
    if (!isMaximized) { prevBoxRef.current = box; setIsMaximized(true); }
    else { setIsMaximized(false); setBox(prevBoxRef.current); }
  };
  const restoreFromDock = (e) => { e?.stopPropagation?.(); setIsMinimized(false); };

  // ----- Actions -----
  const safeName = (str) =>
    (str || "document").replace(/[\\/:*?"<>|]+/g, " ").replace(/\s+/g, " ").trim().slice(0, 120);

  const doDownload = async () => {
    // Use whatever URL we already resolved as a fallback for navigation
    const bestExistingUrl =
      downloadUrl ||
      genericUrl ||
      (typeof pdfSource === "string" ? pdfSource : "");

    try {
      setDownloading(true);

      // keep your analytics hook
      if (typeof onDownload === "function") {
        const fmt =
          /\.pdf($|\?)/i.test(bestExistingUrl || "") ? "pdf" :
          /\.docx($|\?)/i.test(bestExistingUrl || "") ? "docx" :
          /\.txt($|\?)/i.test(bestExistingUrl || "") ? "text" :
          /\.html?($|\?)/i.test(bestExistingUrl || "") ? "html" :
          /\.png($|\?)/i.test(bestExistingUrl || "") ? "png" :
          /\.(jpe?g)($|\?)/i.test(bestExistingUrl || "") ? "jpg" :
          /\.csv($|\?)/i.test(bestExistingUrl || "") ? "csv" :
          /\.xlsx($|\?)/i.test(bestExistingUrl || "") ? "xlsx" :
          /\.xls($|\?)/i.test(bestExistingUrl || "") ? "xls" : "bin";
        await onDownload(fmt, bestExistingUrl || "");
      }

      // Pull node/owner/token from the record or the current URL (public viewer case)
      const qs = new URLSearchParams(window.location.search);
      const nodeId = record?.id || record?.nodeId || qs.get("nodeId");
      const owner = (record?.ownerEmail || record?.owner || qs.get("owner") || qs.get("ownerEmail") || "")
        .toLowerCase().trim();
      const token = record?.shareToken || record?.token || qs.get("token");

      // If we’re on a shared/public link, use the public POST to fetch a signed URL, then navigate to it
      if (nodeId && owner && token && BACKEND_BASE) {
        // Try as attachment (download) first
        let res = await fetch(`${BACKEND_BASE}/vault/transfer/download-url-public`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "omit",
          body: JSON.stringify({ nodeId, owner, token, dl: 1, asAttachment: 1 }),
        });

        // If owner disabled download, retry without attachment to at least open inline
        if (res.status === 403) {
          res = await fetch(`${BACKEND_BASE}/vault/transfer/download-url-public`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "omit",
            body: JSON.stringify({ nodeId, owner, token }),
          });
        }

        if (!res.ok) {
          const txt = await res.text().catch(() => "");
          throw new Error(txt || `Request failed (${res.status})`);
        }

        const data = await res.json();
        if (!data?.url) throw new Error("No signed URL returned.");

        const a = document.createElement("a");
        a.href = data.url;
        a.target = "_blank";
        a.rel = "noopener";
        document.body.appendChild(a);
        a.click();
        a.remove();
        return;
      }

      // Fallback: if we don’t have nodeId/owner/token, just open the best existing URL
      if (bestExistingUrl) {
        const a = document.createElement("a");
        a.href = bestExistingUrl;
        a.target = "_blank";
        a.rel = "noopener";
        document.body.appendChild(a);
        a.click();
        a.remove();
        return;
      }

      alert("No downloadable asset is available for this document.");
    } catch (e) {
      alert(e?.message || "Download failed.");
    } finally {
      setDownloading(false);
    }
  };

  // Print (unchanged from your version)
  const doPrint = async () => {
    if (printingRef.current) return;
    printingRef.current = true;
    const release = () => { printingRef.current = false; setTimeout(() => (printingRef.current = false), 0); };

    try {
      const officeSrc =
        officeEmbedUrl ||
        (htmlDoc ? extractOfficeSrcFromHtml(htmlDoc) : null) ||
        null;
      if (officeSrc) {
        const w = window.open(officeSrc, "_blank", "noopener,noreferrer");
        if (!w) alert("Please allow popups to print this document from the Office viewer.");
        return;
      }

      if (htmlBody) {
        const doc = `<!doctype html><html><head><meta charset="utf-8" />
          <title>${escapeHtml(title)}</title>
          <style>
            @page { size: Letter; margin: 1in; }
            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; background:#fff; }
            h1,h2,h3,h4 { page-break-after: avoid; margin: 0 0 .4em 0; }
            p { margin: 0 0 .8em 0; }
            table { border-collapse: collapse; width: 100%; margin: 0 0 .8em 0; }
            th, td { border: 1px solid #ddd; padding: 6px 8px; }
            thead { display: table-header-group; }
          </style></head><body>${htmlBody}</body></html>`;
        await printHtmlInHiddenFrame(doc);
        return;
      }

      if (htmlDoc) {
        const imgInDoc = extractFirstImageSrcFromHtml(htmlDoc);
        if (imgInDoc) {
          await printHtmlInHiddenFrame(imageHtmlDoc(imgInDoc));
          return;
        }
      }
      const imgSrc = [downloadUrl, genericUrl].find((u) =>
        isImageUrl(u || "")
      ) || null;
      if (imgSrc) {
        await printHtmlInHiddenFrame(imageHtmlDoc(imgSrc));
        return;
      }

      if (genericUrl) {
        const w = window.open(genericUrl, "_blank", "noopener,noreferrer");
        if (!w) alert("Please allow popups to print this document.");
        return;
      }

      alert("No printable content available.");
    } finally {
      release();
    }
  };

  const decZoom = () => setZoom((z) => Math.max(50, z - 10));
  const incZoom = () => setZoom((z) => Math.min(200, z + 10));
  const resetZoom = () => setZoom(100);

  // ----- Render -----
  const genericHtmlDoc =
    !isPdfRecord && !isOfficeRecord ? htmlDoc : null;

  const hasRenderable = Boolean(
    isPdfRecord ||
      htmlBody ||
      genericHtmlDoc ||
      officeEmbedUrl ||
      genericUrl
  );

  const renderBody = () => {
    currentViewKindRef.current = null;

    if (isPdfRecord) {
      currentViewKindRef.current = "pdf";

      return (
        <div style={s.pdfViewerWrap}>
          <PdfViewer
            src={pdfSource}
            title={title}
            filename={
              record?.filename ||
              record?.name ||
              title ||
              "document.pdf"
            }
            downloadUrl={pdfDownloadUrl}
            fallbackUrl={explicitBrowserFallbackUrl}
            getBrowserFallbackUrl={browserFallbackProvider}
            httpHeaders={fileAccessHttpHeaders}
            withCredentials={fileAccessWithCredentials}
            allowDownload={allowRecordDownload}
            allowPrint={true}
            allowFullscreen={true}
            allowBrowserFallback={true}
            showTitle={false}
            defaultSidebarOpen={true}
            initialZoom="fit-width"
            onDownload={
              !isFileAccessGatewayRecord &&
              typeof onDownload === "function"
                ? handlePdfDownload
                : undefined
            }
          />
        </div>
      );
    }

    if (htmlBody) {
      currentViewKindRef.current = "html-generic";
      return (
        <div style={s.pageCenter}>
          <div style={s.pageOuter}>
            <div
              style={s.page}
              className="doc-viewer-page"
              dangerouslySetInnerHTML={{ __html: htmlBody }}
            />
          </div>
        </div>
      );
    }

    if (officeEmbedUrl) {
      currentViewKindRef.current = "office";
      return (
        <div style={s.iframeWrap}>
          <iframe
            ref={viewerFrameRef}
            title="Document Viewer (Microsoft Office)"
            src={officeEmbedUrl}
            style={s.iframe}
            referrerPolicy="no-referrer"
          />
        </div>
      );
    }

    if (genericHtmlDoc) {
      const looksImg = /<img\b/i.test(genericHtmlDoc);
      currentViewKindRef.current = looksImg
        ? "html-img"
        : "html-generic";

      return (
        <div style={s.iframeWrap}>
          <div style={s.iframeOuterZoomable}>
            <iframe
              ref={viewerFrameRef}
              title="Document Viewer (HTML)"
              srcDoc={genericHtmlDoc}
              style={s.iframe}
            />
          </div>
        </div>
      );
    }

    if (genericUrl) {
      currentViewKindRef.current = "url";
      return (
        <div style={s.iframeWrap}>
          <iframe
            ref={viewerFrameRef}
            title="Document Viewer"
            src={genericUrl}
            style={s.iframe}
            referrerPolicy="no-referrer"
          />
        </div>
      );
    }

    if (!fallbackDelayPassed) {
      return <div style={s.loading}>Loading preview…</div>;
    }

    return (
      <div style={{ padding: 24, color: "#6b7280", fontSize: 14 }}>
        This published document has no embeddable preview. Use{" "}
        <strong>Download</strong> to view it.
      </div>
    );
  };

  // PdfViewer owns PDF zoom. Microsoft owns Office zoom. The modal's legacy
  // zoom is retained only for inline HTML and image previews.
  const showZoomControls =
    !isPdfRecord &&
    !isOfficeRecord &&
    Boolean(htmlBody || genericHtmlDoc);

  return (
    <>
      {/* Minimized dock */}
      <div ref={dockRef} style={s.dock}>
        <div
          style={s.dockHandle}
          title="Drag"
          aria-label="Drag"
          onPointerDown={(e) => {
            e.stopPropagation();
            try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch {}
            const el = dockRef.current;
            const rect = el.getBoundingClientRect();
            dockDragRef.current = {
              startX: e.clientX,
              startY: e.clientY,
              startRight: window.innerWidth - rect.right,
              startBottom: window.innerHeight - rect.bottom,
            };
          }}
          onPointerMove={(e) => {
            if (!dockDragRef.current) return;
            const el = dockRef.current;
            const ds = dockDragRef.current;
            const newRight = Math.max(8, ds.startRight + (ds.startX - e.clientX));
            const newBottom = Math.max(8, ds.startBottom + (ds.startY - e.clientY));
            el.style.right = `${newRight}px`;
            el.style.bottom = `${newBottom}px`;
          }}
          onPointerUp={(e) => {
            try { e.currentTarget.releasePointerCapture?.(e.pointerId); } catch {}
            dockDragRef.current = null;
          }}
        >
          ⋮⋮
        </div>
        <span style={s.dockTitle}>{title}</span>
        <button
          style={s.dockBtn}
          onClick={restoreFromDock}
          onPointerDown={(e) => e.stopPropagation()}
          aria-label="Restore"
        >
          Restore
        </button>
        <button
          style={s.dockBtn}
          onClick={doClose}
          onPointerDown={(e) => e.stopPropagation()}
          aria-label="Close"
        >
          Close
        </button>
      </div>

      {/* Modal */}
      <div
        style={s.backdrop}
        role="dialog"
        aria-modal="true"
        aria-label="Document Viewer"
        onPointerDown={(e) => {
          if (e.target !== e.currentTarget) {
            backdropPointerRef.current = null;
            return;
          }

          backdropPointerRef.current = {
            pointerId: e.pointerId,
            x: e.clientX,
            y: e.clientY,
          };
        }}
        onPointerUp={(e) => {
          const start = backdropPointerRef.current;
          backdropPointerRef.current = null;

          if (!start || e.target !== e.currentTarget) return;
          if (start.pointerId !== e.pointerId) return;

          const dx = e.clientX - start.x;
          const dy = e.clientY - start.y;
          const distance = Math.hypot(dx, dy);

          // Only a stationary, intentional backdrop click closes the viewer.
          // Scroll, drag, swipe, and trackpad interactions cannot close it.
          if (distance <= 6) onClose?.();
        }}
        onPointerCancel={() => {
          backdropPointerRef.current = null;
        }}
      >
        <div style={s.modal} onMouseDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>
          {/* Header (drag handle) */}
          <div
            style={s.header}
            onPointerDown={onDragStart}
            onPointerMove={onDragMove}
            onPointerUp={onDragEnd}
            onPointerCancel={onDragEnd}
          >
            <div style={s.titleWrap}>
              <div style={s.titleStyle} title={title}>{title}</div>
              <div style={s.meta}>{createdAtLabel || ""}</div>
            </div>

            <div
              style={s.winControls}
              onPointerDown={(e) => e.stopPropagation()}
            >
              {!isMobileTablet ? (
                <>
                  <button
                    type="button"
                    style={s.ctrlBtn}
                    title="Minimize"
                    aria-label="Minimize"
                    onClick={doMinimize}
                  >
                    ▁
                  </button>
                  <button
                    type="button"
                    style={s.ctrlBtn}
                    title={isMaximized ? "Restore" : "Maximize"}
                    aria-label={isMaximized ? "Restore" : "Maximize"}
                    onClick={doMaximize}
                  >
                    {isMaximized ? "❐" : "□"}
                  </button>
                </>
              ) : null}
              <button
                type="button"
                style={{ ...s.ctrlBtn, ...s.ctrlClose }}
                title="Close"
                aria-label="Close"
                onClick={doClose}
              >
                ×
              </button>
            </div>
          </div>

          {!isPdfRecord ? (
            <>
              {/* Non-PDF toolbar. PdfViewer supplies the complete PDF toolbar. */}
              <div style={s.toolbar}>
                <div style={s.leftGroup}>
                  {showZoomControls ? (
                    <>
                      <button
                        type="button"
                        style={s.btn}
                        onClick={decZoom}
                        aria-label="Zoom out"
                      >
                        −
                      </button>
                      <span style={s.pill}>{zoom}%</span>
                      <button
                        type="button"
                        style={s.btn}
                        onClick={incZoom}
                        aria-label="Zoom in"
                      >
                        +
                      </button>
                      <button
                        type="button"
                        style={s.btn}
                        onClick={resetZoom}
                        aria-label="Reset zoom"
                      >
                        Reset
                      </button>
                    </>
                  ) : null}
                </div>

                <div style={s.rightGroup}>
                  <button
                    type="button"
                    style={s.btn}
                    onClick={doPrint}
                    disabled={!hasRenderable && !canDownload}
                  >
                    Print
                  </button>
                  <button
                    type="button"
                    style={s.primary}
                    onClick={doDownload}
                    disabled={downloading || !canDownload}
                    title={
                      canDownload
                        ? ""
                        : "No downloadable file available"
                    }
                  >
                    {downloading ? "Preparing…" : "Download"}
                  </button>
                </div>
              </div>
            </>
          ) : null}

          {/* Content */}
          <div style={s.contentWrap}>{renderBody()}</div>
        </div>
      </div>
    </>
  );
}

/* ===================== Helpers ===================== */


function isOfficeLikeUrl(value = "") {
  const source = String(value || "");
  return (
    /\.(docx?|xlsx?|pptx?|rtf)(\?|$)/i.test(source) ||
    /wordprocessingml|spreadsheetml|presentationml|msword|ms-excel|ms-powerpoint|rtf/i.test(
      source
    )
  );
}

function isOfficeEmbedUrl(value = "") {
  return /https:\/\/view\.officeapps\.live\.com\/op\/embed\.aspx/i.test(
    String(value || "")
  );
}

function isOfficeContentType(value = "") {
  return /msword|wordprocessingml|spreadsheetml|presentationml|ms-excel|ms-powerpoint|rtf/i.test(
    String(value || "")
  );
}

function isPdfContentType(value = "") {
  return /application\/pdf/i.test(String(value || ""));
}

function isPdfUrl(u = "") {
  const s = String(u || "");
  return (
    /\.pdf(\?|$)/i.test(s) ||
    /application\/pdf/i.test(s) ||
    /(^(?:[^?]+)\?|[?&])(contenttype|contentType|response-content-type|mime|mimeType|type)=application\/pdf/i.test(
      s
    )
  );
}
function isImageUrl(u = "") {
  const s = String(u || "");
  return (
    /\.(png|jpe?g|gif|webp|bmp|svg)(\?|$)/i.test(s) ||
    /(^(?:[^?]+)\?|[?&])(contenttype|contentType|response-content-type|mime|mimeType|type)=image\//i.test(
      s
    )
  );
}

function getViewportPadding(viewportWidth) {
  return viewportWidth < MOBILE_TABLET_BREAKPOINT
    ? MOBILE_TABLET_PADDING
    : DESKTOP_PADDING;
}

function calcBox(vw, vh) {
  const padding = getViewportPadding(vw);

  if (vw < MOBILE_TABLET_BREAKPOINT) {
    return {
      left: 0,
      top: 0,
      width: Math.max(320, vw),
      height: Math.max(360, vh),
    };
  }

  const innerW = Math.max(
    320,
    Math.min(MAX_W, vw - 2 * padding)
  );
  const innerH = Math.max(
    360,
    Math.min(MAX_H, vh - 2 * padding)
  );
  const left = Math.round((vw - innerW) / 2);
  const top = Math.round((vh - innerH) / 2);

  return clampTo(
    left,
    top,
    innerW,
    innerH,
    vw,
    vh,
    padding
  );
}
function clampBox(b, vw, vh, pad) {
  return clampTo(b.left, b.top, b.width, b.height, vw, vh, pad);
}
function clampTo(left, top, width, height, vw, vh, pad) {
  const minL = pad, minT = pad;
  const maxL = Math.max(pad, vw - pad - width);
  const maxT = Math.max(pad, vh - pad - height);
  return {
    left: Math.min(Math.max(left, minL), maxL),
    top: Math.min(Math.max(top, minT), maxT),
    width,
    height,
  };
}
function escapeHtml(str) {
  return String(str).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function sanitizeFilename(name) {
  return name.replace(/[\\/:*?"<>|]+/g, " ").replace(/\s+/g, " ").trim().slice(0, 180);
}
function parseContentDispositionFilename(cd) {
  if (!cd) return "";
  const star = cd.match(/filename\*\s*=\s*([^;]+)/i);
  if (star) {
    const v = star[1].trim().replace(/^UTF-8''/i, "");
    try { return decodeURIComponent(v); } catch { return v; }
  }
  const plain = cd.match(/filename\s*=\s*"(.*?)"/i) || cd.match(/filename\s*=\s*([^;]+)/i);
  return plain ? plain[1].trim() : "";
}
function guessExtFrom(x) {
  const s = String(x || "").toLowerCase();
  if (/application\/pdf|\.pdf($|\?)/.test(s)) return ".pdf";
  if (/vnd\.openxmlformats-officedocument\.wordprocessingml\.document|\.docx($|\?)/.test(s)) return ".docx";
  if (/msword|\.doc($|\?)/.test(s)) return ".doc";
  if (/text\/plain|\.txt($|\?)/.test(s)) return ".txt";
  if (/html|\.html?($|\?)/.test(s)) return ".html";
  if (/image\/png|\.png($|\?)/.test(s)) return ".png";
  if (/image\/jpe?g|\.jpe?g($|\?)/.test(s)) return ".jpg";
  if (/image\/gif|\.gif($|\?)/.test(s)) return ".gif";
  if (/text\/csv|\.csv($|\?)/.test(s)) return ".csv";
  if (/vnd\.ms-excel|\.xls($|\?)/.test(s)) return ".xls";
  if (/vnd\.openxmlformats-officedocument\.spreadsheetml\.sheet|\.xlsx($|\?)/.test(s)) return ".xlsx";
  return "";
}

function decodeHtmlAttribute(value) {
  if (!value) return "";

  const textarea = document.createElement("textarea");
  textarea.innerHTML = String(value);
  return textarea.value;
}

function extractOfficeSrcFromHtml(html) {
  const match = String(html || "").match(
    /src=['"]([^'"]*officeapps\.live\.com[^'"]*)['"]/i
  );
  return match ? match[1] : null;
}

function extractPdfSrcFromHtml(html) {
  const s = String(html || "");
  const mEmbed = s.match(/<embed\b([^>]+)>/i);
  if (mEmbed) {
    const attrs = mEmbed[1];
    if (/type=['"]application\/pdf['"]/i.test(attrs)) {
      const mSrc = attrs.match(/\s(?:src|data)=['"]([^'"]+)['"]/i);
      if (mSrc) return mSrc[1];
    }
  }
  const mObj = s.match(/<object\b([^>]+)>/i);
  if (mObj) {
    const attrs = mObj[1];
    if (/type=['"]application\/pdf['"]/i.test(attrs)) {
      const mData = attrs.match(/\s(?:data|src)=['"]([^'"]+)['"]/i);
      if (mData) return mData[1];
    }
  }
  const mIframe = s.match(/<iframe\b[^>]*\s(?:src)=['"]([^'"]+)['"]/i);
  if (mIframe && isPdfUrl(mIframe[1])) return mIframe[1];
  return null;
}
function extractFirstImageSrcFromHtml(html) {
  const m = String(html || "").match(/<img[^>]+src=['"]([^'"]+)['"]/i);
  return m ? m[1] : null;
}


/* ---------- Print wrappers ---------- */

function imageHtmlDoc(src) {
  return [
    "<!doctype html>",
    "<html><head><meta charset='utf-8'/>",
    "<meta name='viewport' content='width=device-width, initial-scale=1'/>",
    "<style>",
    "html,body{margin:0;padding:0;height:100%;background:#fff;}",
    "body{display:flex;align-items:flex-start;justify-content:center;}",
    "img{max-width:100%;height:auto;display:block;}",
    "@page { size:auto; margin:0.5in; }",
    "</style></head><body>",
    `<img src="${src}" alt="preview" />`,
    "</body></html>",
  ].join("");
}



/* ---------- Print engine (hidden iframe, single attempt) ---------- */
function printHtmlInHiddenFrame(html) {
  return new Promise((resolve) => {
    const iframe = document.createElement("iframe");
    Object.assign(iframe.style, {
      position: "fixed",
      left: "0",
      top: "0",
      width: "100vw",
      height: "100vh",
      border: "0",
      opacity: "0",
      pointerEvents: "none",
      zIndex: "-1",
      background: "#fff",
    });
    document.body.appendChild(iframe);

    const win = iframe.contentWindow;
    const doc = win.document;

    let cleaned = false;
    const cleanup = () => {
      if (cleaned) return;
      cleaned = true;
      try { detachAfterPrint(win); } catch {}
      document.body.removeChild(iframe);
      resolve();
    };

    attachAfterPrint(win, cleanup);

    try {
      doc.open(); doc.write(html); doc.close();
      setTimeout(() => { try { win.focus(); win.print(); } catch {} }, 350);
    } catch {
      setTimeout(cleanup, 1200);
    }

    setTimeout(cleanup, 15000);
  });
}

function attachAfterPrint(win, cleanup) {
  const mm = window.matchMedia && window.matchMedia("print");
  const onMM = (mql) => { if (!mql.matches) setTimeout(cleanup, 120); };
  if (typeof win.addEventListener === "function") win.addEventListener("afterprint", cleanup);
  if (mm) {
    if (typeof mm.addEventListener === "function") mm.addEventListener("change", onMM);
    else if (typeof mm.addListener === "function") mm.addListener(onMM);
  }
  win.___onAfterPrintCleanup = { mm, onMM, cleanup };
}

function detachAfterPrint(win) {
  try { win.removeEventListener?.("afterprint", win.___onAfterPrintCleanup?.cleanup); } catch {}
  const mm = win.___onAfterPrintCleanup?.mm;
  const onMM = win.___onAfterPrintCleanup?.onMM;
  if (mm && onMM) {
    try {
      if (typeof mm.removeEventListener === "function") mm.removeEventListener("change", onMM);
      else if (typeof mm.removeListener === "function") mm.removeListener(onMM);
    } catch {}
  }
  delete win.___onAfterPrintCleanup;
}
